import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import { Col, Divider, Form, Radio, Row, Select, Typography } from 'antd';

import { PageProps } from '@/types/PageProps';
import { FloatingFormItem, SaveButton } from '@/components/_common';

import {
  RegistrationsDefaultCode,
  UpdateMatrixSettingsRequest,
} from '@/types/Register';
import {
  useMatrix,
  useMatrixUpdate,
} from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import { useEffect } from 'react';
import { useWindowSize } from '@/hooks';

const { Title } = Typography;

interface RegistrationsSettingsFormValues {
  defaultPartCode: RegistrationsDefaultCode;
  defaultPartApplication: 0 | 1;
  vehicleKmIsRequired: boolean;
}

function RegistrationsSettingsPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<RegistrationsSettingsFormValues>();
  const { width } = useWindowSize();

  const { data } = useMatrix();
  const mutationEdit = useMatrixUpdate();

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        defaultPartCode: data.matrixSettings.defaultPartCode,
        defaultPartApplication: data.matrixSettings.defaultPartApplication
          ? 0
          : 1,
        vehicleKmIsRequired: data.matrixSettings.vehicleKmIsRequired,
      });
    }
  }, [data]);

  const handleEditSettings = async () => {
    const values = await form.validateFields();

    const booleanMap = {
      0: true,
      1: false,
    };

    if (data) {
      const obj: UpdateMatrixSettingsRequest = {
        id: data.id,
        matrixSettings: {
          ...values,
          id: data.matrixSettings.id,
          defaultPartApplication: booleanMap[values.defaultPartApplication],
        },
      };
      mutationEdit.mutateAsync(obj);
    }
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <div className="page">
        <div className="page-header">
          <Title level={4}>{intl.formatMessage({ id: route.name })}</Title>
          {!(width < 992) && (
            <SaveButton
              onClick={handleEditSettings}
              loading={mutationEdit.isLoading}
            >
              {intl.formatMessage({
                id: 'commons.form.button.save',
              })}
            </SaveButton>
          )}
        </div>

        <Divider />

        <Form form={form} style={{ marginTop: '24px' }}>
          <Row gutter={[16, 12]}>
            <Col span={24}>
              <Title level={5}>
                Selecione um código padrão para consultas de peças e insumos
              </Title>

              <Form.Item name="defaultPartCode">
                <Radio.Group
                  optionType="button"
                  buttonStyle="solid"
                  size="large"
                >
                  <Radio value={RegistrationsDefaultCode.manufacturerCode}>
                    Cód. fabricante
                  </Radio>
                  <Radio value={RegistrationsDefaultCode.barCode}>
                    Cód. barras
                  </Radio>
                  <Radio value={RegistrationsDefaultCode.internalCode}>
                    Cód. interno
                  </Radio>
                  <Radio value={RegistrationsDefaultCode.companyCode}>
                    Cód. empresa
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Col>

            <Col span={24} lg={12} xxl={6}>
              <FloatingFormItem
                name="defaultPartApplication"
                label="Tipo aplicação de peças e serviços"
              >
                <Select
                  allowClear
                  style={{ width: '100%' }}
                  options={[
                    {
                      value: 0,
                      label: 'Cadastros',
                    },
                    {
                      value: 1,
                      label: 'Livre',
                    },
                  ]}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24}>
              <Title level={5}>
                Tornar o Km do cadastro de veículo obrigatório?
              </Title>

              <Form.Item name="vehicleKmIsRequired">
                <Radio.Group
                  optionType="button"
                  buttonStyle="solid"
                  size="large"
                >
                  <Radio value={true}>Obrigatório</Radio>
                  <Radio value={false}>Opcional</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {width < 992 && (
          <SaveButton
            block
            onClick={handleEditSettings}
            loading={mutationEdit.isLoading}
          >
            {intl.formatMessage({
              id: 'commons.form.button.save',
            })}
          </SaveButton>
        )}
      </div>
    </>
  );
}

export default RegistrationsSettingsPage;
